import { useState, useEffect, useRef, FormEvent } from 'react';
import { useAuthStore } from '../hooks/useAuth';
import { Mail, CheckCircle, Send } from 'lucide-react';
import toast from 'react-hot-toast';

interface InboxThread {
    appointmentId: string;
    carrierName: string;
    carrierMcNumber: string;
    customerPoNumber: string;
    unreadCount: number;
    lastMessageAt: string;
    lastMessageSnippet: string;
}

interface ThreadMessage {
    id: string;
    content: string;
    createdAt: string;
    senderType: 'warehouse' | 'carrier' | 'system';
    readAt: string | null;
}

export function Inbox() {
    const { token } = useAuthStore();

    const [threads, setThreads] = useState<InboxThread[]>([]);
    const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
    const [messages, setMessages] = useState<ThreadMessage[]>([]);
    const [isLoadingThreads, setIsLoadingThreads] = useState(true);
    // const [isLoadingMessages, setIsLoadingMessages] = useState(false); // Unused for now
    const [replyText, setReplyText] = useState('');
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Fetch Threads
    const fetchThreads = async (isBackground = false) => {
        if (!token) return;
        try {
            if (!isBackground) setIsLoadingThreads(true);
            const res = await fetch('/api/messages/inbox', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setThreads(data.threads || []);
            }
        } catch (error) {
            console.error('Failed to fetch inbox threads', error);
        } finally {
            if (!isBackground) setIsLoadingThreads(false);
        }
    };

    // Poll Threads
    useEffect(() => {
        fetchThreads();
        const interval = setInterval(() => fetchThreads(true), 15000);
        return () => clearInterval(interval);
    }, [token]);

    // Fetch Messages for Selected Thread
    useEffect(() => {
        if (!selectedThreadId || !token) {
            setMessages([]);
            return;
        }

        const fetchMessages = async () => {
            try {
                // setIsLoadingMessages(true);
                const res = await fetch(`/api/messages/thread/${selectedThreadId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setMessages(data.messages || []);
                    scrollToBottom();

                    // Optimistic read update
                    markAsRead(selectedThreadId);
                }
            } catch (error) {
                toast.error("Failed to load conversation");
            } finally {
                // setIsLoadingMessages(false);
            }
        };

        fetchMessages();
    }, [selectedThreadId, token]);

    // Mark as Read
    const markAsRead = async (threadId: string) => {
        try {
            await fetch(`/api/messages/thread/${threadId}/mark-read`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            // Update thread list locally to clear badge
            setThreads(prev => prev.map(t =>
                t.appointmentId === threadId ? { ...t, unreadCount: 0 } : t
            ));
        } catch (err) {
            // silent
        }
    };

    // Scroll to bottom
    const scrollToBottom = () => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    // Send Reply (uses existing endpoint)
    const handleSend = async (e: FormEvent) => {
        e.preventDefault();
        if (!replyText.trim() || !selectedThreadId) return;

        try {
            const res = await fetch(`/api/appointments/${selectedThreadId}/messages`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ content: replyText })
            });

            if (res.ok) {
                const data = await res.json();
                // Append message locally
                setMessages(prev => [...prev, data.message]);
                setReplyText('');
                scrollToBottom();

                // Refresh threads to update snippet/time
                fetchThreads(true);
            } else {
                toast.error("Failed to send message");
            }
        } catch (error) {
            console.error('Send failed', error);
        }
    };

    const selectedThread = threads.find(t => t.appointmentId === selectedThreadId);

    return (
        <div className="flex h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 overflow-hidden">
            {/* Left Column: Thread List */}
            <div className="w-1/3 min-w-[320px] max-w-[400px] border-r border-slate-200 dark:border-slate-800 flex flex-col bg-white dark:bg-slate-900 shadow-sm">
                {/* Header */}
                <div className="h-16 border-b border-slate-200 dark:border-slate-800 flex items-center pl-14 pr-6 md:px-6 justify-between shrink-0">
                    <h1 className="text-xl font-bold flex items-center gap-2">
                        <Mail className="w-5 h-5 text-primary" />
                        Inbox
                    </h1>
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-2">
                    {isLoadingThreads && threads.length === 0 && (
                        <div className="text-center py-10 text-muted-foreground">Loading...</div>
                    )}

                    {!isLoadingThreads && threads.length === 0 && (
                        <div className="text-center py-10 text-muted-foreground">No messages yet.</div>
                    )}

                    {threads.map(thread => (
                        <div
                            key={thread.appointmentId}
                            onClick={() => setSelectedThreadId(thread.appointmentId)}
                            className={`
                                p-4 rounded-xl cursor-pointer transition-all duration-200 border
                                ${selectedThreadId === thread.appointmentId
                                    ? 'bg-blue-50 dark:bg-slate-800 border-blue-200 dark:border-slate-700 shadow-sm'
                                    : 'bg-transparent border-transparent hover:bg-slate-100 dark:hover:bg-slate-800/50'
                                }
                            `}
                        >
                            <div className="flex justify-between items-start mb-1">
                                <span className={`font-semibold text-sm ${thread.unreadCount > 0 ? 'text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-400'}`}>
                                    {thread.carrierName || 'Unknown Carrier'}
                                </span>
                                <span className="text-[10px] text-muted-foreground whitespace-nowrap ml-2">
                                    {new Date(thread.lastMessageAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>
                            <div className="flex justify-between items-center text-xs text-muted-foreground mb-1">
                                <span>PO: {thread.customerPoNumber}</span>
                                {thread.unreadCount > 0 && (
                                    <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                                        {thread.unreadCount}
                                    </span>
                                )}
                            </div>
                            <div className="text-xs text-slate-600 dark:text-slate-400 line-clamp-1">
                                {thread.lastMessageSnippet}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Right Column: Conversation */}
            <div className="flex-1 flex flex-col bg-slate-50 dark:bg-slate-950">
                {selectedThread ? (
                    <>
                        {/* Chat Header */}
                        <div className="h-16 border-b border-slate-200 dark:border-slate-800 flex items-center px-6 justify-between bg-white dark:bg-slate-900 shrink-0">
                            <div>
                                <h2 className="text-sm font-bold flex items-center gap-2">
                                    {selectedThread.carrierName}
                                    <span className="font-normal text-muted-foreground">({selectedThread.carrierMcNumber})</span>
                                </h2>
                                <p className="text-xs text-muted-foreground">PO #{selectedThread.customerPoNumber}</p>
                            </div>
                        </div>

                        {/* Messages Area */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-4">
                            {messages.map(msg => (
                                <div
                                    key={msg.id}
                                    className={`flex ${msg.senderType === 'warehouse' ? 'justify-end' : 'justify-start'}`}
                                >
                                    <div
                                        className={`
                                            max-w-[70%] p-3 rounded-2xl text-sm shadow-sm
                                            ${msg.senderType === 'warehouse'
                                                ? 'bg-blue-600 text-white rounded-tr-none'
                                                : 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-700 rounded-tl-none'
                                            }
                                        `}
                                    >
                                        <p>{msg.content}</p>
                                        <div className={`text-[10px] mt-1 flex items-center justify-end gap-1 ${msg.senderType === 'warehouse' ? 'text-blue-100' : 'text-slate-400'}`}>
                                            {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            {msg.senderType === 'warehouse' && (
                                                msg.readAt ? <CheckCircle className="w-3 h-3" /> : <CheckCircle className="w-3 h-3 opacity-50" />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input Area */}
                        <div className="p-4 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 shrink-0">
                            <form onSubmit={handleSend} className="flex gap-2">
                                <input
                                    type="text"
                                    value={replyText}
                                    onChange={(e) => setReplyText(e.target.value)}
                                    placeholder="Type a message..."
                                    className="flex-1 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full px-4 text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                                />
                                <button
                                    type="submit"
                                    disabled={!replyText.trim()}
                                    className="p-2 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Send className="w-5 h-5" />
                                </button>
                            </form>
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
                        <Mail className="w-16 h-16 mb-4 opacity-20" />
                        <p>Select a conversation to start reading</p>
                    </div>
                )}
            </div>
        </div>
    );
}
